import type { FloorplanRoom } from "./types";
import { getRoomInfo } from "./furniture/roomInfo";

/** Blocks a host can leave out because it renders them itself (the admin
 *  floorplan's room panel has its own header, badges, tenant and wayfinding sections). */
export type FloorplanRoomDetailBlock = "header" | "badges" | "tenant" | "navigation";

interface FloorplanRoomDetailProps {
  room: FloorplanRoom;
  className?: string;
  /** Blocks to leave out; everything is shown by default. */
  hide?: FloorplanRoomDetailBlock[];
  /** Management actions for tenant-assignable rooms */
  onAssignTenant?: (roomId: number) => void;
  onRemoveOccupancy?: (occupancyId: number) => void;
  /** Quick book action (member portal) */
  onQuickBook?: (roomId: number, start: string, end: string) => void;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

const sectionTitle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: "var(--mantine-color-dimmed)",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 4,
};

/** "14:05" today, "Oct 22, 14:05" on another day. */
export function formatSince(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  const time = when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  return when.toDateString() === new Date().toDateString()
    ? time
    : `${when.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

export function FloorplanRoomDetail({ room, className = "", hide = [], onAssignTenant, onRemoveOccupancy, onQuickBook }: FloorplanRoomDetailProps) {
  const info = getRoomInfo(room);
  const displayName = room.description || room.name;
  const subtitle = [
    room.description ? room.name : null,
    room.floorName,
    room.addressName,
  ].filter(Boolean).join(" · ");
  const shown = (block: FloorplanRoomDetailBlock) => !hide.includes(block);

  return (
    <div className={className} style={{ fontSize: 13 }}>
      {/* Header */}
      {shown("header") && (
        <>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{displayName}</div>
          <div style={{ color: "var(--mantine-color-dimmed)", fontSize: 11, marginBottom: 8 }}>{subtitle}</div>
        </>
      )}

      {/* Availability badge */}
      {room.isBookable && room.isAvailable != null && (
        <div style={{ marginBottom: 8 }}>
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 9999,
              fontSize: 12,
              fontWeight: 600,
              color: "var(--mantine-color-white)",
              backgroundColor: room.isAvailable ? "var(--mantine-color-green-filled)" : "var(--mantine-color-red-filled)",
            }}
          >
            {room.isAvailable ? "Available" : "Booked"}
          </span>
        </div>
      )}

      {/* Presence sensor: in use / free; "since" only comes with the staff payload. */}
      {room.inUse != null && (
        <div style={{ marginBottom: 8, fontSize: 12 }} data-testid="room-in-use">
          <strong>{room.inUse ? "In use" : "Free"}</strong>
          {room.inUse && room.inUseSince ? ` since ${formatSince(room.inUseSince)}` : ""}
        </div>
      )}

      {/* Current event */}
      {!room.isAvailable && room.currentEvent && (
        <div
          style={{
            marginBottom: 8,
            padding: 8,
            borderRadius: 6,
            border: "1px solid var(--mantine-color-red-outline)",
            backgroundColor: "var(--mantine-color-red-light)",
          }}
        >
          <div style={sectionTitle}>Current booking</div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{room.currentEvent.title || "Booked"}</div>
          <div style={{ color: "var(--mantine-color-dimmed)", fontSize: 12 }}>
            {formatTime(room.currentEvent.start)} – {formatTime(room.currentEvent.end)}
          </div>
        </div>
      )}

      {/* Free slot info */}
      {room.isBookable && room.nextFreeSlotStart && room.nextFreeSlotDurationMinutes != null && (
        <div style={{ color: "var(--mantine-color-dimmed)", fontSize: 12, marginBottom: 8 }}>
          ⏱ {room.isAvailable
            ? `Free for ${formatDuration(room.nextFreeSlotDurationMinutes)}`
            : `Free in ${formatDuration(Math.max(0, Math.round((new Date(room.nextFreeSlotStart).getTime() - Date.now()) / 60000)))}`}
        </div>
      )}

      {/* New neighbour / new member badges */}
      {shown("badges") && (room.isNewNeighbour || room.isNewMember) && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {room.isNewMember && (
            <span title="This tenant joined within the last 14 days" style={{ fontSize: 11, padding: "2px 8px", borderRadius: 9999, backgroundColor: "var(--mantine-color-cyan-light)", color: "var(--mantine-color-cyan-light-color)", cursor: "help" }}>
              🎉 New Member
            </span>
          )}
          {room.isNewNeighbour && !room.isNewMember && (
            <span title="This tenant moved into this room within the last 7 days" style={{ fontSize: 11, padding: "2px 8px", borderRadius: 9999, backgroundColor: "var(--mantine-color-cyan-light)", color: "var(--mantine-color-cyan-light-color)", cursor: "help" }}>
              ✨ New Neighbour
            </span>
          )}
        </div>
      )}

      {/* Occupancy — shown unless explicitly disabled */}
      {shown("tenant") && room.isTenantAssignable !== false && (
        <div style={{ marginBottom: 8 }}>
          <div style={sectionTitle}>Tenant</div>
          {/* Active tenants */}
          {room.occupancies && room.occupancies.length > 0 ? (
            room.occupancies.map((occ, i) => (
              <div key={i} style={{ fontSize: 12, marginBottom: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontWeight: 500 }}>{occ.tenantName}</span>
                  {occ.endDate && (
                    <span style={{ color: "var(--mantine-color-dimmed)", fontSize: 11 }}>→ {occ.endDate}</span>
                  )}
                  {onRemoveOccupancy && occ.id && (
                    <button
                      onClick={() => onRemoveOccupancy(occ.id)}
                      style={{ fontSize: 10, color: "var(--mantine-color-red-light-color)", background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: "auto" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {/* Its own line: in the narrow panel it would squeeze the tenant row into columns. */}
                {occ.lease && occ.lease.roomIds.length > 1 && (
                  <div title={occ.lease.labelLong} style={{ color: "var(--mantine-color-dimmed)", fontSize: 11 }}>
                    {`Rented together: ${occ.lease.labelMember}`}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{ color: "var(--mantine-color-dimmed)", fontSize: 12, fontStyle: "italic" }}>No tenant assigned</div>
          )}

          {/* Upcoming tenants */}
          {room.upcomingOccupancies && room.upcomingOccupancies.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {room.upcomingOccupancies.map((occ, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, marginBottom: 3, color: "var(--mantine-color-blue-light-color)" }}>
                  <span>{occ.startDate} →</span>
                  <span style={{ fontWeight: 500 }}>{occ.tenantName}</span>
                </div>
              ))}
            </div>
          )}

          {/* Assign tenant — always last, always available */}
          {onAssignTenant && (
            <button
              onClick={() => onAssignTenant(room.id)}
              style={{ fontSize: 11, color: "var(--mantine-color-blue-light-color)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", display: "block", marginTop: 6 }}
            >
              + Assign tenant
            </button>
          )}
        </div>
      )}

      {/* Quick book — rendered by consumer via children or onQuickBook */}
      {onQuickBook && room.isAvailable && room.nextFreeSlotStart && room.nextFreeSlotDurationMinutes != null && (
        <div style={{ marginBottom: 8 }}>
          <div style={sectionTitle}>Quick book</div>
          {/* Consumer provides QuickBookButtons from @nodus/booking */}
          <div style={{ display: "flex", gap: 4 }}>
            {([15, 30, 60] as const)
              .filter((d) => d <= room.nextFreeSlotDurationMinutes!)
              .map((d) => {
                const endIso = new Date(new Date(room.nextFreeSlotStart!).getTime() + d * 60000).toISOString();
                return (
                  <button
                    key={d}
                    onClick={() => onQuickBook(room.id, room.nextFreeSlotStart!, endIso)}
                    style={{
                      fontSize: 11, padding: "3px 8px", borderRadius: 4,
                      backgroundColor: "var(--mantine-color-green-filled)", color: "var(--mantine-color-white)", border: "none",
                      cursor: "pointer", fontWeight: 500,
                    }}
                  >
                    {d >= 60 ? `${d / 60}h` : `${d}m`}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Amenities */}
      {(info.seats > 0 || room.squareMeters != null || info.hasCamera || info.hasTv || info.hasSpeaker || info.hasCoffee || info.hasDishwasher || info.hasWhiteboard) && (
        <div style={{ marginBottom: 8 }}>
          <div style={sectionTitle}>Amenities</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {room.squareMeters != null && (
              <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>📐 ~{Math.floor(room.squareMeters!)} m²</div>
            )}
            {(info.seats > 0 || room.seats != null) && (
              <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>👥 {info.seats || room.seats} seats</div>
            )}
            {info.hasCamera && <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>📷 Camera</div>}
            {info.hasTv && <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>🖥 Display</div>}
            {info.hasSpeaker && <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>🔊 Speaker</div>}
            {info.hasCoffee && <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>☕ Coffee</div>}
            {info.hasDishwasher && <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>🍽 Dishwasher</div>}
            {info.hasWhiteboard && <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)" }}>📝 Whiteboard</div>}
          </div>
          {Object.entries(info.amenities).filter(([, s]) => s.broken).map(([key, status]) => (
            <div key={key} style={{ fontSize: 11, color: "var(--mantine-color-yellow-light-color)", marginTop: 2 }}>
              ⚠️ {key} broken{status.repairEta ? ` — ETA: ${status.repairEta}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Navigation hints */}
      {shown("navigation") && room.navigationHints && (
        <div style={{ marginBottom: 8 }}>
          <div style={sectionTitle}>How to find</div>
          <div style={{ fontStyle: "italic", color: "var(--mantine-color-dimmed)", fontSize: 12 }}>
            {room.navigationHints}
          </div>
        </div>
      )}
    </div>
  );
}
